import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { Router, RouterModule } from '@angular/router';
import { ToolbarComponent } from '../../../shared/components/toolbar/toolbar.component';
import { ToastService } from '../../../shared/services/toast.service';
import { IDigitalProductBillingType } from '../../../digitalProduct/models/digitalProductBillingType.model';
import { IDigitalProductRecurringInterval } from '../../../digitalProduct/models/digitalProductRecurringInterval.model';
import { IDigitalProductVisibility } from '../../../digitalProduct/models/digitalProductVisibility.model';
import { IDigitalProduct } from '../../../digitalProduct/models/digitalProduct.model';
import { SubscriptionService } from '../../services/subscription.service';
import { subscriptionActions } from '../../store/subscription.actions';
import { getSubscriptionsSelector } from '../../store/subscription.selectors';

@Component({
    selector: 'clp-subscription-list',
    styleUrls: ['./subscriptionList.component.scss'],
    templateUrl: './subscriptionList.component.html',
    imports: [
        RouterModule,
        TranslateModule,
        CommonModule,
        ToolbarComponent
    ],
    providers: [
        SubscriptionService,
        ToastrService
    ]
})
export class SubscriptionListComponent implements OnInit {
    private subscriptionService = inject(SubscriptionService);
    private router = inject(Router);
    private toastService = inject(ToastService);
    private translate = inject(TranslateService);
    private store = inject(Store);

    subscriptions$!: Observable<IDigitalProduct[]>;
    searchTerm = '';
    visibilityFilter: 'all' | '1' | '2' | '3' = 'all';
    subscriptionToDelete: IDigitalProduct | null = null;
    deleting = false;
    private readonly visibilityMap: Record<string, IDigitalProductVisibility> = {
        '1': IDigitalProductVisibility.Draft,
        'Draft': IDigitalProductVisibility.Draft,
        '2': IDigitalProductVisibility.Published,
        'Published': IDigitalProductVisibility.Published,
        '3': IDigitalProductVisibility.Archived,
        'Archived': IDigitalProductVisibility.Archived
    };
    private readonly recurringIntervalMap: Record<string, IDigitalProductRecurringInterval> = {
        '0': IDigitalProductRecurringInterval.None,
        'None': IDigitalProductRecurringInterval.None,
        '1': IDigitalProductRecurringInterval.Day,
        'Day': IDigitalProductRecurringInterval.Day,
        '2': IDigitalProductRecurringInterval.Week,
        'Week': IDigitalProductRecurringInterval.Week,
        '3': IDigitalProductRecurringInterval.Month,
        'Month': IDigitalProductRecurringInterval.Month,
        '4': IDigitalProductRecurringInterval.Year,
        'Year': IDigitalProductRecurringInterval.Year
    };

    ngOnInit(): void {
        this.store.dispatch(subscriptionActions.getSubscriptions());
        this.subscriptions$ = this.store.select(getSubscriptionsSelector);
    }

    editSubscription(id: string | undefined): void {
        this.router.navigate(['updateSubscription', id]);
    }

    openDeleteConfirmation(subscription: IDigitalProduct): void {
        if (!subscription.id) {
            return;
        }

        this.subscriptionToDelete = subscription;
    }

    closeDeleteConfirmation(): void {
        if (this.deleting) {
            return;
        }

        this.subscriptionToDelete = null;
    }

    confirmDeleteSubscription(): void {
        const id = this.subscriptionToDelete?.id;
        if (!id) {
            this.closeDeleteConfirmation();
            return;
        }

        this.deleting = true;
        this.subscriptionService.deleteSubscription(id).subscribe({
            next: () => {
                this.deleting = false;
                this.subscriptionToDelete = null;
                this.toastService.show(this.translate.instant('subscription.delete.successMessage'), '');
                this.store.dispatch(subscriptionActions.getSubscriptions());
            },
            error: () => {
                this.deleting = false;
            }
        });
    }

    @HostListener('document:keydown.escape')
    onEscapeKey(): void {
        this.closeDeleteConfirmation();
    }

    getCountByVisibility(digitalProducts: IDigitalProduct[], visibility: IDigitalProductVisibility): number {
        return digitalProducts.filter(digitalProduct => this.resolveVisibility(digitalProduct.visibility) === visibility).length;
    }

    onSearchChange(event: Event): void {
        this.searchTerm = (event.target as HTMLInputElement).value;
    }

    onVisibilityFilterChange(event: Event): void {
        this.visibilityFilter = (event.target as HTMLSelectElement).value as 'all' | '1' | '2' | '3';
    }

    getFilteredSubscriptions(subscriptions: IDigitalProduct[]): IDigitalProduct[] {
        const search = this.searchTerm.trim().toLowerCase();
        const selectedVisibility = this.visibilityFilter;
        const selectedVisibilityValue = selectedVisibility === 'all'
            ? null
            : this.resolveVisibility(selectedVisibility);

        return subscriptions.filter(subscription => {
            const name = (subscription.name ?? '').toLowerCase();
            const description = (subscription.description ?? '').toLowerCase();
            const currency = (subscription.currency ?? '').toLowerCase();
            const matchesSearch = !search || name.includes(search) || description.includes(search) || currency.includes(search);
            const matchesVisibility = selectedVisibilityValue === null
                || this.resolveVisibility(subscription.visibility) === selectedVisibilityValue;

            return matchesSearch && matchesVisibility;
        });
    }

    getVisibilityBadgeClass(visibility: unknown): string {
        switch (this.resolveVisibility(visibility)) {
            case 2:
                return 'border-emerald-500/40 bg-emerald-500/15 text-emerald-300';
            case 3:
                return 'border-amber-500/40 bg-amber-500/15 text-amber-300';
            case 1:
            default:
                return 'border-slate-600 bg-slate-700/50 text-slate-200';
        }
    }

    getBillingTypeBadgeClass(): string {
        return 'border-sky-500/40 bg-sky-500/15 text-sky-300';
    }

    isRecurring(): boolean {
        return true;
    }

    getBillingTypeLabelKey(): string {
        return `subscription.billingType.${IDigitalProductBillingType.Recurring}`;
    }

    getVisibilityLabelKey(visibility: unknown): string {
        return `subscription.visibility.${this.resolveVisibility(visibility) ?? IDigitalProductVisibility.Draft}`;
    }

    getRecurringIntervalLabelKey(recurringInterval: unknown): string {
        return `subscription.recurringInterval.${this.resolveRecurringInterval(recurringInterval) ?? IDigitalProductRecurringInterval.None}`;
    }

    private resolveVisibility(value: unknown): IDigitalProductVisibility | undefined {
        return this.resolveEnumValue(value, this.visibilityMap);
    }

    private resolveRecurringInterval(value: unknown): IDigitalProductRecurringInterval | undefined {
        return this.resolveEnumValue(value, this.recurringIntervalMap);
    }

    private resolveEnumValue<T extends number>(value: unknown, map: Record<string, T>): T | undefined {
        if (value === undefined || value === null) {
            return undefined;
        }

        const normalized = String(value).trim();
        return map[normalized];
    }
}
